import { TCoreButton } from 'core/global/CoreButton';
import { TCoreAddToCartMixinInstance } from './CoreAddToCartMixin';

/**
 * @description Add to Cart Button widget
 * @param Button CoreButton widget for extending
 * @returns Add to Cart Button class
 */
function ProductActionButtonClassCreator(Button: TCoreButton) {
    class ProductActionButton extends Button {
        prefs() {
            return {
                checkCanSubmitAddToCart: true,
                submitUrl: null,
                pid: null,
                submitEvent: null,
                submitEventData: null,
                ...super.prefs()
            };
        }

        /**
         * @description Handles button click events with validation logic
         */
        handleClick() {
            if (this.disabled || this.isBusy) {
                return;
            }

            if (this.prefs().checkCanSubmitAddToCart) {
                this.emit('checksubmitaddtocart', this.onCheckSubmitAddToCart.bind(this));
            } else {
                this.submit();
            }
        }

        /**
         * @description Updates product ID in preferences
         * @param productId Product ID to be set
         */
        updateProductId(productId) {
            this.setPref('pid', productId);
        }

        /**
         * @description Callback handler for add to cart validation check
         * @param widget - The widget instance performing validation
         * @param result - Validation result object
         * @param result.canSubmit - Whether the submission can proceed
         */
        onCheckSubmitAddToCart(widget: TCoreAddToCartMixinInstance, result: { canSubmit: boolean }) {
            if (result.canSubmit) {
                const currentProductId = widget.prefs().currentProductId;

                if (currentProductId) {
                    this.updateProductId(currentProductId);
                }

                this.submit();
            }
        }

        /**
         * @description Submits the form/action with configured URL and product ID
         * Handles URL construction with product ID parameter and emits configured events
         */
        submit() {
            const { submitEvent, eventData } = this.getModalParams();

            if (submitEvent) {
                this.eventBus().emit(submitEvent, eventData);
            }
        }

        /**
         * @description Constructs modal parameters including URL with product ID and event data
         * @returns An object containing submitEvent and eventData
         */
        getModalParams() {
            const { submitUrl, pid, submitEvent, submitEventData } = this.prefs();

            const actionUrl = submitUrl ? new URL(submitUrl) : null;
            const eventData: Record<string, unknown> = { ...(submitEventData || {}) };

            if (actionUrl && pid) {
                actionUrl.searchParams.append('pid', pid);
                eventData.url = actionUrl.toString();
            }

            return { submitEvent, eventData };
        }
    }

    return ProductActionButton;
}

export type TProductActionButton = ReturnType<typeof ProductActionButtonClassCreator>;

export type TProductActionButtonInstance = InstanceType<TProductActionButton>;

export default ProductActionButtonClassCreator;
